import React, { useState, useEffect } from "react";
import styles from "./Filters.module.css";
import MultiSelectFilter from "../../../components/MultiSelectFilter/MultiSelectFilter";
import TextInputFilter from "../../../components/TextInputFilter/TextInputFilter";
import { useCountries } from "../../../hooks/useCountries";
import { HighestQualification, AcademicStandingAuthor, YearsExperience } from "../../../utils/constants";

export default function FiltersAuthor({ role, onFilterChange, currentFilters }) {
  const [localFilters, setLocalFilters] = useState({
    regions: [],
    highestQualification: [],
    hasPublications: false,
    hasPhd: false,
    academicStanding: [],
    researchExperience: []
  });
  const { countries } = useCountries();

  useEffect(() => {
    // Only sync searchExpert from parent, manage other filters locally
    setLocalFilters(prev => ({ ...prev, searchExpert: currentFilters.searchExpert || "" }));
  }, [currentFilters.searchExpert]);

  // Reset filters when role changes
  useEffect(() => {
    const resetFilters = {
      regions: [],
      highestQualification: [],
      hasPublications: false,
      hasPhd: false,
      academicStanding: [],
      researchExperience: []
    };
    setLocalFilters(resetFilters);
    onFilterChange(resetFilters);
  }, [role]);


  // Convert country values back to country objects for display
  const getCountryObjects = (countryValues) => {
    if (!countryValues || !Array.isArray(countryValues) || countryValues.length === 0) {
      return [];
    }
    
    if (!countries || countries.length === 0) {
      return countryValues.map(value => ({ label: value, value }));
    }
    
    return countryValues.map(value => {
      const country = countries.find(country => country.value === value);
      return country || { label: value, value };
    });
  };

  // Convert filter values back to objects for display
  const getFilterObjects = (filterValues, filterOptions) => {
    if (!filterValues || !Array.isArray(filterValues) || filterValues.length === 0) {
      return [];
    }
    
    if (!filterOptions || filterOptions.length === 0) {
      return filterValues.map(value => ({ label: value, value }));
    }
    
    return filterValues.map(value => {
      const option = filterOptions.find(option => option.value === value);
      return option || { label: value, value };
    });
  };

  const handleMultiSelectChange = (filterKey, selectedItems) => {
    const updatedFilters = { ...localFilters, [filterKey]: selectedItems };
    setLocalFilters(updatedFilters);
    // Send combined filters to parent
    onFilterChange({ ...currentFilters, ...updatedFilters });
  };

  const handleCheckboxChange = (filterKey, value) => {
    const updatedFilters = { ...localFilters, [filterKey]: value };
    setLocalFilters(updatedFilters);
    // Send combined filters to parent
    onFilterChange({ ...currentFilters, ...updatedFilters });
  };

  const handleAcademicStandingTextApply = (value) => {
    // Convert text value to array format similar to previous implementation
    const academicStandingArray = value.trim() ? [value] : [];
    const updatedFilters = { ...localFilters, academicStanding: academicStandingArray };
    setLocalFilters(updatedFilters);
    // Send combined filters to parent
    onFilterChange({ ...currentFilters, ...updatedFilters });
  };

  const handleAcademicStandingTextClear = () => {
    const updatedFilters = { ...localFilters, academicStanding: [] };
    setLocalFilters(updatedFilters);
    // Send combined filters to parent
    onFilterChange({ ...currentFilters, ...updatedFilters });
  };

  const handleHighestQualificationTextApply = (value) => {
    // Convert text value to array format similar to previous implementation
    const highestQualificationArray = value.trim() ? [value] : [];
    const updatedFilters = { ...localFilters, highestQualification: highestQualificationArray };
    setLocalFilters(updatedFilters);
    // Send combined filters to parent
    onFilterChange({ ...currentFilters, ...updatedFilters });
  };

  const handleHighestQualificationTextClear = () => {
    const updatedFilters = { ...localFilters, highestQualification: [] };
    setLocalFilters(updatedFilters);
    // Send combined filters to parent
    onFilterChange({ ...currentFilters, ...updatedFilters });
  };

  const handleClearAllFilters = () => {
    const resetFilters = {
      regions: [],
      highestQualification: [],
      hasPublications: false,
      hasPhd: false,
      academicStanding: [],
      researchExperience: [],
      searchExpert: ""
    };
    setLocalFilters(resetFilters);
    onFilterChange(resetFilters);
  };
  return (
    <div className={styles.filters}>
      <div className={styles.container}>
        <button className={styles.filterBtn}> Filters</button>
        <MultiSelectFilter
          dropdownTitle="Country"
          placeholder="Search Country..."
          fetchData={(query) =>
            Promise.resolve(
              countries.filter((country) =>
                country.label.toLowerCase().includes(query.toLowerCase())
              )
            )
          }
          selected={getCountryObjects(localFilters.regions)}
          onSelectionChange={(selected) =>
            handleMultiSelectChange("regions", selected)
          }
        />
       
        <TextInputFilter
          dropdownTitle="Highest Qualification"
          placeholder="Search highest qualification..."
          value={localFilters.highestQualification.length > 0 ? localFilters.highestQualification[0] : ""}
          onApply={handleHighestQualificationTextApply}
          onClear={handleHighestQualificationTextClear}
        />
        
        <TextInputFilter
          dropdownTitle="Academic Standing"
          placeholder="Search academic standing..."
          value={localFilters.academicStanding.length > 0 ? localFilters.academicStanding[0] : ""}
          onApply={handleAcademicStandingTextApply}
          onClear={handleAcademicStandingTextClear}
        />
        <MultiSelectFilter
          dropdownTitle="Research Experience"
          placeholder="Search research experience..."
          fetchData={(query) =>
            Promise.resolve(
              YearsExperience.filter((s) =>
                s.label.toLowerCase().includes(query.toLowerCase())
              )
            )
          }
          selected={getFilterObjects(localFilters.researchExperience, YearsExperience)}
          onSelectionChange={(selected) => handleMultiSelectChange('researchExperience', selected)}
        />
         <label>
          <input
            type="checkbox"
            checked={localFilters.hasPublications}
            onChange={(e) => handleCheckboxChange('hasPublications', e.target.checked)}
          /> Has Publications
        </label>
        <label>
          <input
            type="checkbox"
            checked={localFilters.hasPhd}
            onChange={(e) => handleCheckboxChange('onlyPhd', e.target.checked)}
          /> Only Ph.D.
        </label>
        <button 
          className={styles.clearBtn}
          onClick={handleClearAllFilters}
        >
          Clear Filters
        </button>
      </div>  
    </div>
  );
}
